import { TableSchema } from './component-library/DataTableComponent/types-v4'
import { JsonFormSchemaV2, JsonFormPropertyV2 } from './component-library/FormComponent/types'

/**
 * Converts a TableSchema to a JsonFormSchemaV2 for use in forms
 * @param tableSchema The table schema to convert
 * @param defaultValues Optional default values for the form fields
 * @returns A JsonFormSchemaV2 that can be used with the FormComponent
 */
export const tableToFormSchema = (
  tableSchema: TableSchema,
  defaultValues?: Record<string, unknown>
): JsonFormSchemaV2 => {
  // Extract editable columns from the table schema
  const editableColumns = tableSchema.columns.filter((column) => column.columnConfig.editing?.editable !== false)

  // Convert table columns to form properties
  const formProperties: JsonFormPropertyV2[] = editableColumns
    .map((column) => {
      const editing = column.columnConfig.editing

      // Skip columns without editing configuration
      if (!editing) {
        return null
      }

      // Create the form property from the column definition
      const formProperty: JsonFormPropertyV2 = {
        id: column.id,
        title: column.title,
        type: editing.type || 'string',
        description: column.description,
        readonly: editing.readonly,
        widget: editing.widget,
        items: editing.items,
        choices: editing.choices,
        format: editing.format,
        minimum: editing.minimum,
        maximum: editing.maximum,
        minLength: editing.minLength,
        maxLength: editing.maxLength,
        pattern: editing.pattern,
      }

      // Add default value if provided
      if (defaultValues && defaultValues[column.id] !== undefined) {
        formProperty.default = defaultValues[column.id]
      } else if (editing.default !== undefined) {
        formProperty.default = editing.default
      }

      return formProperty
    })
    .filter(Boolean) as JsonFormPropertyV2[]

  // Get required fields
  const requiredFields = editableColumns
    .filter((column) => column.columnConfig.editing?.required)
    .map((column) => column.id)

  // Create the form schema
  const formSchema: JsonFormSchemaV2 = {
    type: 'object',
    title: tableSchema.tableConfig.title,
    description: tableSchema.tableConfig.description,
    properties: formProperties,
    required: requiredFields.length > 0 ? requiredFields : undefined,
  }

  return formSchema
}
